import {
  makeDatesByDaysInterval,
  makeDatesByWeeksInterval
} from '../../common/DateTime/components/ScheduleDateTime/utils';
import {
  BONUSES_EXPIRED_TYPES,
  PROMO_FREQUENCY_STATES,
  PROMO_LIMITATION_STATES,
  SCHEDULING_TYPE
} from '../../constants';
import { Bonus, BonusDetails, DateHelper, ValidBonus } from '../../store/types';

const getValidBonuses = (
  promoBonuses: Bonus[],
  userParticipatedBonuses: BonusDetails[],
  f: DateHelper
): ValidBonus[] | [] =>
  promoBonuses
    ?.map((bonus) => {
      const {
        bonusTemplateId,
        isBonusRepeatable,
        periodEnd,
        periodStart,
        nDays,
        frequency,
        setSchedule,
        repetitionsTotal,
        limitPerPlayer = 0,
        period,
        startTime,
        endTime,
        repeatEvery,
        daysOfWeek
      } = bonus;
      const startTimeInMinutes = startTime
        ?.split(':')
        .reduce((acc, time) => 60 * acc + +time, 0);
      const endTimeInMinutes = endTime
        ?.split(':')
        .reduce((acc, time) => 60 * acc + +time, 0);
      const currentDate = f.getDateTime();
      const periodEndDate = setSchedule
        ? f
            .getDateTime({ date: periodEnd })
            .startOf('day')
            .plus({
              minutes: f.getDateTime().o + endTimeInMinutes
            })
        : f.getDateTime({ date: periodEnd });
      const periodStartDate = setSchedule
        ? f
            .getDateTime({ date: periodStart })
            .startOf('day')
            .plus({
              minutes: f.getDateTime().o + startTimeInMinutes
            })
        : f.getDateTime({ date: periodStart });
      let isBonusFinished = periodEndDate < currentDate;

      const currentBonus = userParticipatedBonuses?.find(
        ({ bonusTemplateId: bTId }) => bTId === Number(bonusTemplateId)
      );

      if (setSchedule) {
        const possibleDates =
          period === SCHEDULING_TYPE.DAY
            ? makeDatesByDaysInterval({
                f,
                startDate: periodStart,
                endDate: periodEnd,
                startTimeInMinutes,
                endTimeInMinutes,
                daysInterval: repeatEvery
              })
            : makeDatesByWeeksInterval({
                f,
                startDate: periodStart,
                endDate: periodEnd,
                startTimeInMinutes,
                endTimeInMinutes,
                weeksInterval: repeatEvery,
                daysOfWeek
              });
        isBonusFinished = periodEndDate < currentDate || !possibleDates.length;
      }

      if (currentBonus) {
        const {
          stateDesc,
          bonusId,
          freespinId,
          bonusTime = {
            sec: 0
          },
          bonusCount: crmBonusCount,
          bonusSourceType,
          bonuses = [],
          startedAt,
          allowPlayerToParticipate
        } = currentBonus;

        const { sec: bonusActivationTime } = bonusTime || {};
        const bonusActivationDateTime = bonusSourceType?.startsWith('bs')
          ? f.getDateTime({
              date: bonuses.length ? bonuses[0]?.createdAt : startedAt,
              fromUTC: true
            })
          : f.getDateTime({
              date: bonusActivationTime * 1000
            });
        const possibleBonusActivationTime = bonusSourceType?.startsWith('bs')
          ? f
              .getDateTime({
                date: bonuses.length ? bonuses[0]?.createdAt : startedAt,
                fromUTC: true
              })
              ?.plus({
                days:
                  bonuses.length &&
                  frequency === PROMO_FREQUENCY_STATES.ONCE_PER_N_DAYS
                    ? nDays
                    : 0
              })
          : f
              .getDateTime({
                date: bonusActivationTime * 1000
              })
              ?.plus({
                days:
                  frequency === PROMO_FREQUENCY_STATES.ONCE_PER_N_DAYS
                    ? nDays
                    : 0
              });
        const isBsBonusUnavailable =
          bonusSourceType?.startsWith('bs') &&
          (!bonuses.length ||
            BONUSES_EXPIRED_TYPES.includes(bonuses[0]?.status)) &&
          !allowPlayerToParticipate; // condition for bs bonus due to specific bonus response
        const bonusStatus = stateDesc || bonuses[0]?.status;
        const bonusCount = crmBonusCount || bonuses?.length;
        const isBonusFinishedByLimitation =
          bonusActivationDateTime > periodStartDate &&
          possibleBonusActivationTime > periodEndDate &&
          BONUSES_EXPIRED_TYPES.includes(bonusStatus);

        if (setSchedule) {
          const possibleDates =
            period === SCHEDULING_TYPE.DAY
              ? makeDatesByDaysInterval({
                  f,
                  startDate: periodStart,
                  endDate: periodEnd,
                  startTimeInMinutes,
                  endTimeInMinutes,
                  daysInterval: repeatEvery
                })
              : makeDatesByWeeksInterval({
                  f,
                  startDate: periodStart,
                  endDate: periodEnd,
                  startTimeInMinutes,
                  endTimeInMinutes,
                  weeksInterval: repeatEvery,
                  daysOfWeek,
                  possibleBonusActivationTime
                });
          isBonusFinished =
            periodEndDate < currentDate || !possibleDates.length;
        }

        if (
          !isBsBonusUnavailable &&
          !(
            repetitionsTotal === PROMO_LIMITATION_STATES.LIMITED &&
            (bonusCount > limitPerPlayer ||
              (bonusCount === limitPerPlayer &&
                BONUSES_EXPIRED_TYPES.includes(bonusStatus)))
          ) &&
          (!BONUSES_EXPIRED_TYPES.includes(bonusStatus) || isBonusRepeatable)
        ) {
          return {
            ...bonus,
            bonusCount,
            bonusId: bonusId || freespinId || bonuses[0]?.id,
            bonusSourceType,
            isBonusFinished: isBonusFinished || isBonusFinishedByLimitation,
            isBonusActive: bonusStatus
              ? !BONUSES_EXPIRED_TYPES.includes(bonusStatus)
              : false,
            ...(frequency === PROMO_FREQUENCY_STATES.ONCE_PER_N_DAYS &&
              bonusActivationDateTime > periodStartDate && {
                possibleBonusActivationTime
              }),
            originalPeriodStart: periodStart,
            ...(frequency === PROMO_FREQUENCY_STATES.ONCE_PER_N_DAYS &&
              !setSchedule &&
              possibleBonusActivationTime > periodStartDate &&
              possibleBonusActivationTime > currentDate &&
              possibleBonusActivationTime < periodEndDate &&
              currentDate > periodStartDate && {
                periodStart: possibleBonusActivationTime
              }),
            ...(isBonusFinishedByLimitation && {
              periodEnd: f.getDateTime({
                date: bonusActivationTime * 1000
              })
            })
          };
        }
        return null;
      }

      return { ...bonus, isBonusFinished };
    })
    .filter((bonus): bonus is ValidBonus => bonus !== null)
    .sort((a, b) => (b.bonusId || 0) - (a.bonusId || 0));

export default getValidBonuses;
